import React from 'react';
import { AlertCircle, CheckCircle2, Clock3, Download, FileText, RefreshCw, Search, SearchX, Upload, XCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/layouts/AdminLayout';
import StudentLayout from '../../components/layouts/StudentLayout';
import Select from '../../components/ui/Select';
import POESubmissionCard from './components/POESubmissionCard';
import POEUploadModal from './components/POEUploadModal';
import POEApprovalWorkflow from './components/POEApprovalWorkflow';
import StudentPOEDashboard from './components/StudentPOEDashboard';
import TrainerPOEDashboard from './components/TrainerPOEDashboard';
import poeService from '../../services/poeService';

const FILTERS = [
  ['All', 'All stages'],
  ['ready', 'Ready to upload'],
  ['awaiting_trainer', 'Awaiting trainer'],
  ['awaiting_department', 'Awaiting department'],
  ['awaiting_final', 'Awaiting final approval'],
  ['approved', 'Approved'],
  ['rejected', 'Returned']
];

const EXTERNAL_FILTERS = [
  ['All', 'All verification statuses'],
  ['unverified', 'Awaiting external verification'],
  ['verified', 'Externally verified'],
];

const readRoleList = value => {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : value.replace(/^\{|\}$/g, '').split(',');
  } catch (_) {
    return value.replace(/^\{|\}$/g, '').split(',');
  }
};

const toRows = value => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.rows)) return value.rows;
  return [];
};

const normaliseProgrammeRow = row => {
  if (!row || typeof row !== 'object') return null;
  const department = row.department && typeof row.department === 'object' ? row.department : null;
  const id = row.id || row.program_id || row.programme_id;
  const name = row.name || row.programme_name || row.programme;
  if (!id || !name) return null;
  return {
    ...row,
    id: String(id),
    name: String(name),
    code: row.code || row.programme_code || '',
    department_id: row.department_id || row.departmentId || department?.id || '',
    department_name: row.department_name || row.departmentName || department?.name || '',
  };
};

const normaliseDepartmentLabel = value => String(value || '')
  .replace(/\s*\([^)]*\)\s*$/, '')
  .trim()
  .toLowerCase();

const POEManagementHub = ({ embedded = false, onBack = null }) => {
  const { profile, user } = useAuth();
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('All');
  const [externalDepartmentFilter, setExternalDepartmentFilter] = React.useState('all');
  const [externalProgrammeFilter, setExternalProgrammeFilter] = React.useState('all');
  const [externalUnitFilter, setExternalUnitFilter] = React.useState('all');
  const [externalCamFilter, setExternalCamFilter] = React.useState('all');
  const [externalYearFilter, setExternalYearFilter] = React.useState('all');
  const [externalSemesterFilter, setExternalSemesterFilter] = React.useState('all');
  const [externalCatalogue, setExternalCatalogue] = React.useState({
    programmes: [],
    departments: [],
    units: [],
    cams: [],
    years: [],
    semesters: [],
  });
  const [uploadOpen, setUploadOpen] = React.useState(false);
  const [uploadAssessmentId, setUploadAssessmentId] = React.useState('');
  const [selectedSubmission, setSelectedSubmission] = React.useState(null);
  const [filePreview, setFilePreview] = React.useState(null);
  const [fileDecisionSaving, setFileDecisionSaving] = React.useState('');
  const [fileDecisionError, setFileDecisionError] = React.useState('');

  const roles = [
    profile?.primary_role,
    ...readRoleList(profile?.secondary_roles),
    user?.user_metadata?.role,
    user?.user_metadata?.primary_role,
    user?.app_metadata?.role,
    user?.app_metadata?.primary_role,
  ]
    .map(role => String(role || '').trim().toLowerCase().replace(/[\s-]+/g, '_'))
    .filter(Boolean);
  const isStudent = roles.includes('student');
  const isTrainer = roles.includes('trainer_lecturer');
  const isExternalVerifier = roles.includes('external_poe_verifier');
  // Institution-wide POE querying is reserved for the dedicated external
  // verifier role. QA and assessment staff continue through the normal
  // workflow/read-only view returned by get_poe_workflow_items().
  const isVerifier = isExternalVerifier;
  const uploadable = items.filter(item => item.can_upload);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (isVerifier) {
        const [dashboardResult, filterResult, programmeResult] = await Promise.allSettled([
          poeService.getExternalDashboardItems(),
          poeService.getExternalFilterOptions(),
          poeService.getExternalProgrammes(),
        ]);
        if (dashboardResult.status === 'rejected') throw dashboardResult.reason;
        const dashboardItems = dashboardResult.value || [];
        const filterOptions = filterResult.status === 'fulfilled' ? filterResult.value : {};
        const programmeRows = programmeResult.status === 'fulfilled' ? programmeResult.value : [];
        const safeFilterOptions = filterOptions && typeof filterOptions === 'object' ? filterOptions : {};
        const catalogueProgrammes = [
          ...toRows(programmeRows),
          ...toRows(safeFilterOptions.programmes),
        ].map(normaliseProgrammeRow).filter(Boolean);
        const programmes = [...new Map(catalogueProgrammes
          .map(row => [String(row.id), row])).values()];
        setItems(dashboardItems);
        setExternalCatalogue({
          ...safeFilterOptions,
          programmes,
        });
      } else {
        setItems(await poeService.getItems());
      }
    } catch (loadError) {
      setItems([]);
      setError(loadError?.message || 'POE evidence could not be loaded. Apply the latest database migration and refresh.');
    } finally {
      setLoading(false);
    }
  }, [isVerifier]);

  React.useEffect(() => { load(); }, [load, profile?.id]);

  const filteredItems = React.useMemo(() => items.filter(item => {
    const query = searchQuery.trim().toLowerCase();
    const matchesSearch = !query || [item.unit_code, item.unit_name, item.student_name, item.student_number, item.programme_name, item.department_name, item.academic_year_name, item.semester_name]
      .some(value => String(value || '').toLowerCase().includes(query));
    const itemStatus = isVerifier
      ? (item.external_verified_at ? 'verified' : 'unverified')
      : (item.evidence_id ? item.workflow_stage : 'ready');
    const selectedDepartment = normaliseDepartmentLabel(externalDepartmentFilter);
    const itemDepartment = normaliseDepartmentLabel(item.department_name);
    const matchesDepartment = !isVerifier || externalDepartmentFilter === 'all'
      || itemDepartment === selectedDepartment
      || String(item.department_id || '') === String(externalDepartmentFilter);
    const matchesProgramme = !isVerifier || externalProgrammeFilter === 'all'
      || String(item.program_id || '') === String(externalProgrammeFilter);
    const matchesUnit = !isVerifier || externalUnitFilter === 'all' || String(item.unit_id || '') === String(externalUnitFilter);
    const matchesCam = !isVerifier || externalCamFilter === 'all'
      || String(item.assessment_number || '') === String(externalCamFilter);
    const matchesYear = !isVerifier || externalYearFilter === 'all' || String(item.academic_year_id || '') === String(externalYearFilter);
    const matchesSemester = !isVerifier || externalSemesterFilter === 'all' || String(item.semester_id || '') === String(externalSemesterFilter);
    return matchesSearch && matchesDepartment && matchesProgramme && matchesUnit && matchesCam && matchesYear && matchesSemester && (statusFilter === 'All' || itemStatus === statusFilter);
  }), [items, searchQuery, statusFilter, isVerifier, externalDepartmentFilter, externalProgrammeFilter, externalUnitFilter, externalCamFilter, externalYearFilter, externalSemesterFilter]);

  const orderedItems = React.useMemo(() => {
    if (!isVerifier) return filteredItems;
    // Keep pending external reviews at the top of the queue. Completed
    // external verifications are retained but serialized at the bottom.
    return [...filteredItems].sort((left, right) => {
      const leftVerified = Boolean(left.external_verified_at);
      const rightVerified = Boolean(right.external_verified_at);
      if (leftVerified !== rightVerified) return leftVerified ? 1 : -1;
      return String(left.student_name || '').localeCompare(String(right.student_name || ''))
        || String(left.unit_code || '').localeCompare(String(right.unit_code || ''))
        || Number(left.assessment_number || 0) - Number(right.assessment_number || 0);
    });
  }, [filteredItems, isVerifier]);

  const externalFilterOptions = React.useMemo(() => {
    const unique = (field, idField = field) => [...new Map(items
      .filter(item => item[field])
      .map(item => [String(item[idField] || item[field]), { id: String(item[idField] || item[field]), name: item[field] }])
    ).values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
    const merge = (catalogueRows, derivedRows) => [...new Map([
      ...(Array.isArray(catalogueRows) ? catalogueRows : []),
      ...(Array.isArray(derivedRows) ? derivedRows : []),
    ].filter(row => row?.id && row?.name).map(row => [String(row.id), { ...row, id: String(row.id) }])).values()]
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    // Keep the department UUID as the option value.  Using the display name
    // here made programme scoping depend on text labels (and silently dropped
    // programmes when a department was renamed or had a legacy label).
    const catalogueDepartments = (Array.isArray(externalCatalogue.departments) ? externalCatalogue.departments : [])
      .filter(row => row?.name && (row?.id || row?.department_id || row?.departmentId))
      .map(row => ({
        ...row,
        id: String(row.id || row.department_id || row.departmentId),
        name: String(row.name),
      }));
    // Departments are an institution hierarchy catalogue, so source them only
    // from the database RPC. Dashboard rows are evidence records and must not
    // add or duplicate department options in this filter.
    const departments = [...new Map(
      catalogueDepartments.map(row => [normaliseDepartmentLabel(row.name), row])
    ).values()];
    return {
      programmes: merge(
        toRows(externalCatalogue.programmes).map(normaliseProgrammeRow).filter(Boolean),
        unique('programme_name', 'program_id')
      ),
      departments,
      units: merge(externalCatalogue.units, [...new Map(items
        .filter(item => item.unit_id)
        .map(item => [String(item.unit_id), {
          id: String(item.unit_id),
          name: [item.unit_code, item.unit_name].filter(Boolean).join(' · '),
          program_id: item.program_id || '',
          program_name: item.programme_name || '',
          department_id: item.department_id || '',
          department_name: item.department_name || '',
        }])
      ).values()]),
      cams: toRows(externalCatalogue.cams),
      years: merge(externalCatalogue.years, unique('academic_year_name', 'academic_year_id')),
      semesters: merge(externalCatalogue.semesters, unique('semester_name', 'semester_id')),
    };
  }, [items, externalCatalogue]);

  const visibleExternalProgrammes = React.useMemo(() => {
    const programmes = externalFilterOptions.programmes;
    if (externalDepartmentFilter === 'all') return programmes;
    const selectedDepartment = normaliseDepartmentLabel(externalDepartmentFilter);
    const scoped = programmes.filter(programme => (
      String(programme.department_id || '') === String(externalDepartmentFilter)
      || normaliseDepartmentLabel(programme.department_name) === selectedDepartment
    ));
    // If an older catalogue response has no programme→department relation,
    // leave the complete programme list available instead of hiding options.
    return scoped.length ? scoped : programmes;
  }, [externalDepartmentFilter, externalFilterOptions.programmes]);

  React.useEffect(() => {
    if (externalProgrammeFilter === 'all') return;
    if (!visibleExternalProgrammes.some(programme => String(programme.id) === String(externalProgrammeFilter))) {
      setExternalProgrammeFilter('all');
    }
  }, [externalProgrammeFilter, visibleExternalProgrammes]);

  const visibleExternalUnits = React.useMemo(() => {
    const units = externalFilterOptions.units;
    const selectedProgramme = String(externalProgrammeFilter || '');
    const selectedDepartment = normaliseDepartmentLabel(externalDepartmentFilter);
    const hasDepartmentRelations = units.some(unit => unit.department_id || unit.department_name);
    const departmentUnits = !hasDepartmentRelations || externalDepartmentFilter === 'all'
      ? units
      : units.filter(unit => (
      String(unit.department_id || '') === String(externalDepartmentFilter)
      || normaliseDepartmentLabel(unit.department_name) === selectedDepartment
      ));
    if (selectedProgramme === 'all') return departmentUnits;
    const hasProgrammeRelations = units.some(unit => unit.program_id || unit.programme_id);
    if (!hasProgrammeRelations) return departmentUnits;
    return departmentUnits.filter(unit => (
      String(unit.program_id || unit.programme_id || '') === selectedProgramme
    ));
  }, [externalDepartmentFilter, externalProgrammeFilter, externalFilterOptions.units]);

  React.useEffect(() => {
    if (externalUnitFilter === 'all') return;
    if (!visibleExternalUnits.some(unit => String(unit.id) === String(externalUnitFilter))) {
      setExternalUnitFilter('all');
    }
  }, [externalUnitFilter, visibleExternalUnits]);

  const visibleExternalCams = React.useMemo(() => {
    const selectedDepartment = normaliseDepartmentLabel(externalDepartmentFilter);
    // CAM options are evidence-backed. The external dashboard only returns
    // POE rows that are available for external review, so a CAM is listed
    // only when an uploaded evidence row exists for the current scope.
    const candidates = items.map(item => ({
      ...item,
      program_id: item.program_id,
      program_name: item.programme_name,
      department_id: item.department_id,
      department_name: item.department_name,
    })).filter(row => row.assessment_number);
    const filtered = candidates.filter(row => {
      if (externalDepartmentFilter !== 'all'
        && normaliseDepartmentLabel(row.department_name) !== selectedDepartment
        && String(row.department_id || '') !== String(externalDepartmentFilter)) return false;
      if (externalProgrammeFilter !== 'all' && String(row.program_id || '') !== String(externalProgrammeFilter)) return false;
      if (externalUnitFilter !== 'all' && String(row.unit_id || '') !== String(externalUnitFilter)) return false;
      if (externalSemesterFilter !== 'all' && row.semester_id && String(row.semester_id) !== String(externalSemesterFilter)) return false;
      return true;
    });
    return [...new Map(filtered.map(row => [String(row.assessment_number), {
      id: String(row.assessment_number),
      name: `CAM ${row.assessment_number}`,
    }])).values()].sort((left, right) => Number(left.id) - Number(right.id));
  }, [items, externalDepartmentFilter, externalProgrammeFilter, externalUnitFilter, externalYearFilter, externalSemesterFilter]);

  React.useEffect(() => {
    if (externalCamFilter === 'all') return;
    if (!visibleExternalCams.some(cam => String(cam.id) === String(externalCamFilter))) {
      setExternalCamFilter('all');
    }
  }, [externalCamFilter, visibleExternalCams]);

  const openUpload = item => {
    setUploadAssessmentId(item?.assessment_id || uploadable[0]?.assessment_id || '');
    setUploadOpen(true);
  };

  const upload = async payload => {
    await poeService.uploadMarkedScript(payload);
    await load();
  };

  const review = async (action, comments) => {
    if (!selectedSubmission?.evidence_id) return;
    await poeService.review({ evidenceId: selectedSubmission.evidence_id, action, comments });
    setSelectedSubmission(null);
    await load();
  };

  const verifyExternally = async comments => {
    if (!selectedSubmission?.evidence_id) return;
    await poeService.verifyExternally({ evidenceId: selectedSubmission.evidence_id, comments });
    setSelectedSubmission(null);
    await load();
  };

  const viewFile = async item => {
    try {
      const url = await poeService.getFilePreviewUrl(item.file_path, { stamped: isExternalVerifier });
      if (!url) throw new Error('The evidence file could not be opened.');
      if (filePreview?.url?.startsWith('blob:')) URL.revokeObjectURL(filePreview.url);
      setFilePreview({
        url,
        title: item.file_name || `${item.unit_code || 'POE'} · CAM ${item.assessment_number || ''}`,
        downloadName: item.file_name || `${item.unit_code || 'poe-evidence'}-${item.assessment_number || 'file'}.pdf`,
        stamped: isExternalVerifier,
        evidenceId: item.evidence_id,
      });
      setFileDecisionError('');
    } catch (viewError) {
      setError(viewError?.message || 'The evidence file could not be opened.');
    }
  };

  const closeFilePreview = () => {
    if (filePreview?.url?.startsWith('blob:')) URL.revokeObjectURL(filePreview.url);
    setFilePreview(null);
  };

  const downloadFilePreview = () => {
    if (!filePreview?.url) return;
    const link = document.createElement('a');
    link.href = filePreview.url;
    link.download = filePreview.downloadName || 'poe-evidence.pdf';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const decideFromFilePreview = async action => {
    if (!isExternalVerifier || !filePreview?.evidenceId || fileDecisionSaving) return;
    let comments = '';
    if (action === 'decline') {
      comments = window.prompt('Reason for declining this POE (required):', '') || '';
      if (!comments.trim()) return;
    }

    setFileDecisionSaving(action);
    setFileDecisionError('');
    try {
      if (action === 'verify') {
        await poeService.verifyExternally({ evidenceId: filePreview.evidenceId });
      } else {
        await poeService.declineExternally({ evidenceId: filePreview.evidenceId, comments });
      }
      closeFilePreview();
      await load();
    } catch (decisionError) {
      setFileDecisionError(decisionError?.message || `The POE could not be ${action === 'verify' ? 'verified' : 'declined'}.`);
    } finally {
      setFileDecisionSaving('');
    }
  };

  const counts = isVerifier ? {
    total: items.length,
    waiting: items.filter(item => !item.external_verified_at).length,
    approved: items.filter(item => Boolean(item.external_verified_at)).length,
    returned: new Set(items.map(item => item.department_name).filter(Boolean)).size,
  } : {
    total: items.filter(item => item.evidence_id).length,
    waiting: items.filter(item => ['awaiting_trainer', 'awaiting_department', 'awaiting_final'].includes(item.workflow_stage)).length,
    approved: items.filter(item => item.workflow_stage === 'approved').length,
    returned: items.filter(item => item.workflow_stage === 'rejected').length
  };

  const statCards = isVerifier ? [
    ['POE records', counts.total, FileText, 'bg-blue-100 text-blue-700'],
    ['Awaiting verification', counts.waiting, Clock3, 'bg-amber-100 text-amber-700'],
    ['Externally verified', counts.approved, CheckCircle2, 'bg-emerald-100 text-emerald-700'],
    ['Departments covered', counts.returned, AlertCircle, 'bg-slate-100 text-slate-700'],
  ] : [
    ['Submitted', counts.total, FileText, 'bg-blue-100 text-blue-700'],
    ['In review', counts.waiting, Clock3, 'bg-amber-100 text-amber-700'],
    ['Approved', counts.approved, CheckCircle2, 'bg-emerald-100 text-emerald-700'],
    ['Returned', counts.returned, AlertCircle, 'bg-red-100 text-red-700'],
  ];

  const content = (
    <div className="space-y-5">
      <section className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm text-blue-700">{isVerifier ? 'POE verification' : 'Assessment evidence'}</p>
          <h1 className="text-2xl font-bold text-slate-950">{isVerifier ? 'Institution-wide POE verification' : 'Portfolio of Evidence'}</h1>
          <p className="mt-1 text-sm text-slate-500">
            {isStudent ? 'Upload the scanned marked script for each completed CAM.' : isVerifier ? 'Query approved evidence across the institution and record external verification stamps.' : 'Verify and approve marked CAM evidence at your assigned workflow stage.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="inline-flex h-10 items-center gap-2 rounded-lg border bg-white px-3 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh</button>
          {isStudent && uploadable.length > 0 && <button onClick={() => openUpload()} className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90"><Upload className="h-4 w-4" />Upload marked script</button>}
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {statCards.map(([label, count, Icon, tone]) => <div key={label} className="flex items-center gap-3 rounded-xl border bg-white p-4 shadow-sm"><span className={`grid h-10 w-10 place-items-center rounded-lg ${tone}`}><Icon className="h-5 w-5" /></span><span><b className="block text-xl text-slate-950">{count}</b><small className="text-slate-500">{label}</small></span></div>)}
      </section>

      <section className="flex flex-col gap-3 rounded-xl border bg-white p-3 shadow-sm sm:flex-row sm:flex-wrap sm:items-center">
        <label className="relative min-w-0 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={searchQuery} onChange={event => setSearchQuery(event.target.value)} placeholder="Search unit, student or programme" className="h-10 w-full rounded-lg border border-slate-300 pl-9 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
        </label>
        {isVerifier && (
          <>
            <select value={externalDepartmentFilter} onChange={event => setExternalDepartmentFilter(event.target.value)} aria-label="Department filter" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 sm:w-auto">
              <option value="all">All departments</option>
              {externalFilterOptions.departments.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
            </select>
            <Select
              aria-label="Programme filter"
              className="w-full sm:min-w-[220px] sm:flex-1 xl:min-w-[260px]"
              value={externalProgrammeFilter}
              onChange={setExternalProgrammeFilter}
              searchable
              placeholder="Select programme"
              options={[
                { value: 'all', label: externalDepartmentFilter === 'all' ? 'All programmes' : 'All programmes in selected department' },
                ...visibleExternalProgrammes.map(option => ({ value: option.id, label: option.name })),
              ]}
            />
            <select value={externalUnitFilter} onChange={event => setExternalUnitFilter(event.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 sm:w-auto">
              <option value="all">All units</option>
              {visibleExternalUnits.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
            </select>
            <select value={externalCamFilter} onChange={event => setExternalCamFilter(event.target.value)} aria-label="CAM filter" className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 sm:w-auto">
              <option value="all">All CAMs</option>
              {visibleExternalCams.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
            </select>
            <select value={externalYearFilter} onChange={event => setExternalYearFilter(event.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 sm:w-auto">
              <option value="all">All academic years</option>
              {externalFilterOptions.years.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
            </select>
            <select value={externalSemesterFilter} onChange={event => setExternalSemesterFilter(event.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 sm:w-auto">
              <option value="all">All semesters</option>
              {externalFilterOptions.semesters.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
            </select>
          </>
        )}
        <select value={statusFilter} onChange={event => setStatusFilter(event.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 sm:w-auto">
          {(isVerifier ? EXTERNAL_FILTERS : FILTERS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </section>

      {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>}

      {loading ? (
        <div className="grid min-h-52 place-items-center rounded-xl border bg-white text-sm text-slate-500">Loading POE evidence…</div>
      ) : orderedItems.length === 0 ? (
        <div className="rounded-xl border bg-white p-10 text-center shadow-sm"><SearchX className="mx-auto h-9 w-9 text-slate-300" /><h2 className="mt-3 font-bold text-slate-900">No POE items found</h2><p className="mt-1 text-sm text-slate-500">{isStudent ? 'A CAM appears here after the trainer saves your mark.' : isVerifier ? 'No institutionally approved evidence matches your query.' : 'There is no evidence at your assigned workflow stage.'}</p></div>
      ) : (
        isVerifier ? (
          <section className="overflow-hidden rounded-xl border bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-[1160px] w-full border-collapse text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="w-12 px-4 py-3 font-semibold">#</th>
                    <th className="px-4 py-3 font-semibold">POE / Unit</th>
                    <th className="px-4 py-3 font-semibold">Student</th>
                    <th className="px-4 py-3 font-semibold">Programme</th>
                    <th className="px-4 py-3 font-semibold">Department</th>
                    <th className="px-4 py-3 font-semibold">Academic period</th>
                    <th className="px-4 py-3 font-semibold">Mark</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 text-right font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {orderedItems.map((item, index) => {
                    const verified = Boolean(item.external_verified_at);
                    return (
                      <tr key={`${item.assessment_id}-${item.evidence_id || 'ready'}`} className="align-top hover:bg-slate-50/70">
                        <td className="px-4 py-4 font-semibold text-slate-500">{index + 1}</td>
                        <td className="max-w-[280px] px-4 py-4">
                          <p className="font-bold text-slate-900">{item.unit_code || '—'} · CAM {item.assessment_number || '—'}</p>
                          <p className="mt-1 text-slate-600">{item.assessment_title || item.unit_name || 'POE evidence'}</p>
                          <p className="mt-1 truncate text-xs text-slate-500" title={item.file_name || ''}>{item.file_name || 'File unavailable'}</p>
                        </td>
                        <td className="px-4 py-4">
                          <p className="font-semibold text-slate-900">{item.student_name || '—'}</p>
                          <p className="mt-1 text-xs text-slate-500">{item.student_number || 'Registration unavailable'}</p>
                        </td>
                        <td className="max-w-[220px] px-4 py-4 text-slate-700">{item.programme_name || '—'}</td>
                        <td className="max-w-[190px] px-4 py-4 text-slate-700">{item.department_name || '—'}</td>
                        <td className="whitespace-nowrap px-4 py-4 text-slate-700">
                          <p>{item.academic_year_name || '—'}</p>
                          <p className="mt-1 text-xs text-slate-500">{item.semester_name || '—'}</p>
                        </td>
                        <td className="whitespace-nowrap px-4 py-4 font-semibold text-slate-900">{item.raw_marks ?? '—'}</td>
                        <td className="px-4 py-4">
                          <span className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${verified ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                            {verified ? 'Externally verified' : 'Awaiting verification'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-right">
                          <button type="button" onClick={() => viewFile(item)} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                            <FileText className="h-4 w-4" />View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        ) : (
          <section className="grid gap-4 xl:grid-cols-2">
            {orderedItems.map(item => <POESubmissionCard key={`${item.assessment_id}-${item.evidence_id || 'ready'}`} submission={item} onOpen={setSelectedSubmission} onView={viewFile} onUpload={openUpload} allowExternalVerify={isExternalVerifier} />)}
          </section>
        )
      )}

      <POEUploadModal isOpen={uploadOpen} onClose={() => setUploadOpen(false)} onSubmit={upload} assessments={uploadable} initialAssessmentId={uploadAssessmentId} />
      <POEApprovalWorkflow isOpen={Boolean(selectedSubmission)} onClose={() => setSelectedSubmission(null)} submission={selectedSubmission} onSubmit={review} onExternalVerify={isVerifier ? verifyExternally : null} onViewFile={() => viewFile(selectedSubmission)} userRole={isStudent ? 'student' : isVerifier ? 'verifier' : profile?.primary_role} />
      {filePreview && (
        <div className="fixed inset-0 z-[1150] flex flex-col bg-slate-950/80 p-4 backdrop-blur-sm sm:p-6">
          <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <h2 className="min-w-0 truncate text-sm font-semibold text-slate-900">{filePreview.title}</h2>
                {filePreview.stamped && <span className="hidden whitespace-nowrap rounded-full bg-emerald-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-emerald-800 sm:inline-flex">Stamped copy</span>}
              </div>
              <div className="ml-4 flex items-center gap-2">
                {filePreview.stamped && (
                  <>
                    <button type="button" onClick={() => decideFromFilePreview('verify')} disabled={Boolean(fileDecisionSaving)} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 disabled:cursor-wait disabled:opacity-60">
                      <CheckCircle2 className="h-4 w-4" />
                      <span className="hidden sm:inline">{fileDecisionSaving === 'verify' ? 'Verifying…' : 'Verify'}</span>
                    </button>
                    <button type="button" onClick={() => decideFromFilePreview('decline')} disabled={Boolean(fileDecisionSaving)} className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:cursor-wait disabled:opacity-60">
                      <XCircle className="h-4 w-4" />
                      <span className="hidden sm:inline">{fileDecisionSaving === 'decline' ? 'Declining…' : 'Decline'}</span>
                    </button>
                  </>
                )}
                <button type="button" onClick={downloadFilePreview} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                  <Download className="h-4 w-4" />
                  <span className="hidden sm:inline">{filePreview.stamped ? 'Download stamped copy' : 'Download file'}</span>
                </button>
                <button type="button" onClick={closeFilePreview} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" aria-label="Close file preview">×</button>
              </div>
            </div>
            {fileDecisionError && <p className="border-b border-red-200 bg-red-50 px-5 py-2 text-xs font-medium text-red-800">{fileDecisionError}</p>}
            <div className="relative min-h-0 flex-1 bg-slate-100">
              <iframe src={filePreview.url} title="Uploaded POE file preview" className="h-full w-full bg-slate-100" />
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (isExternalVerifier) return <AdminLayout title="Portfolio of Evidence Management">{content}</AdminLayout>;

  if (isStudent) {
    if (embedded) return <StudentPOEDashboard />;
    return (
      <StudentLayout title="Portfolio of Evidence">
        <StudentPOEDashboard />
      </StudentLayout>
    );
  }
  
  if (isTrainer) {
    if (embedded) return <TrainerPOEDashboard onBack={onBack} />;
    return (
      <AdminLayout title="Portfolio of Evidence Management">
        <TrainerPOEDashboard />
      </AdminLayout>
    );
  }

  if (embedded) return content;
  return <AdminLayout title="Portfolio of Evidence Management">{content}</AdminLayout>;
};

export default POEManagementHub;
